import type { BuildingGraph, BuildingGraphPlane } from "building-types/building/buildingGraphTypes";
import type { Building } from "building-types/building/buildingTypes";
import type { BuildingGraphFilterBuilding } from "building-types/buildingGraphFilter/buildingGraphFilterBuilding";
import type { Doc } from "building-types/doc";
import type { FramingComponentEntity } from "building-types/framing/framingComponentTypes";
import type {
  EdgeMetadata,
  EdgeMetadataTable,
  FramingBoundaryWithMetadata,
} from "building-types/framing/framingMetaDataTypes";
import type { HeaderAssemblyEntity } from "building-types/framing/headerAssemblyTypes";
import type { WallFramingPickFaceSpec } from "building-types/framing/wallFramingPickFaceSpecTypes";
import type {
  Framing,
  GeneratedFramedWallAssembly,
  WallFramingInput,
  WallFramingMeta,
  WallOpeningBoundary,
  WallOpeningFramingConfigGroup,
  WallOpeningFramingResolver,
} from "building-types/framing/wallFramingTypes";
import { WallFramingType } from "building-types/framing/wallFramingTypes";
import type { Level } from "building-types/levels/levelTypes";
import type { TargetedOpeningSettings } from "building-types/openings/opening";
import type { Room } from "building-types/rooms/roomTypes";
import type { BuildingGenerationIssue } from "building-types/settings/buildingGenerationIssue";
import type { HeaderFramingSettings } from "building-types/settings/headerFramingSettings";
import type { WallPlaneDescriptor, WallPlaneSubdomain } from "building-types/walls2/wallPlane";
import type { WallPlaneFramingLayer } from "building-types/walls2/wallPlaneAssemblyTypes";
import type {
  Framing as OutputWallFramingMember,
  WallFraming,
  WallFramingMeta as OutputWallFramingMeta,
} from "building-types/walls/wallFramingTypes";
import { WallFramingType as OutputWallFramingType } from "building-types/walls/wallFramingTypes";
import type { WallPlaneSides } from "building-types/walls/wallPlaneSideTypes";
import type { RWTable, Table } from "common/data/table";
import { assertIsNotNullOrUndefined } from "common/utils/assertOfType";
import { builtInWallPlaneAssemblies } from "doc/defaults/defaultWallPlaneAssembly";
import { docEntityHandleHash } from "doc/entity/core/handleHash";
import { getFramingProfileDescriptorById, rectangularLumberDescriptor } from "doc/utils/lumberTools";
import { aabb2Center, aabb2Overlaps } from "geom/aabb2";
import type { AABB2 } from "geom/aabb2Types";
import { Vec2PolyLine2ContainmentClass, classifyContainmentVec2PolyLine2 } from "geom/containment";
import { DIST_EPSILON } from "geom/core";
import { planeCopy, planeMoveAlongNormal } from "geom/plane";
import type { Plane } from "geom/planeTypes";
import { polygonUnion } from "geom/polygon";
import {
  polyLine2AABB2,
  polyLine2Copy,
  polyLine2EdgeCount,
  polyLine2FromAABB2,
  polyLine2SetCCW,
  polyLine2SetCW,
} from "geom/polyLine2";
import { closedPolyLine2New } from "geom/polyLine2BrandedTypes";
import type { ClosedPolyLine2 } from "geom/polyLine2BrandedTypes";
import type { PolyLine2Polygon } from "geom/polyLine2Types";
import { faceNodeGetLevelId, planeNormalToZAxisAngle } from "../buildingGraphFilter/entityFilterSelectors";
import { sortedLevelIds } from "../elements/level/levelTools";
import { generateWallFramingWithConvexSplit } from "../elements/structural-framing/wall/wallFramingConvexSplit";
import {
  classifyBoundaryEdgeType,
  computeWallFramingStartPt,
} from "../elements/structural-framing/wall/wallFramingUtils";
import {
  enumeratePlanarTopologySplitPickRegions,
  type PlanarTopologySplitPickRegion,
} from "../elements/walls2/enumeratePlanarTopologySplitPickRegions";
import type { PlanarTopologySplitRegion } from "../elements/walls2/planarTopologySplitRegions";
import { resolveWallPlaneSideRoomsFromFaceNodes } from "../elements/walls2/wallConditionTools";
import { collectWallPlaneFramingLayerBucketsForWallPlane } from "../elements/walls2/wallPlaneSubPlanes";
import type { WallPlaneAssemblyLayerBucketGrid } from "../elements/walls2/wallPlaneSubPlanesTypes";
import { wallFramingNew } from "../elements/walls/wallFraming";
import { getBuildingGraphPlaneTypeFromNormalToZAxisAngle } from "./generateBuildingGraph";
import type {
  FramingGridCell,
  FramingLayerPlacement,
  FramingRegion,
  GridCellUnionPolygon,
} from "./generateWallFramingFromWallPlanesTypes";
import { filterWallOpeningBoundariesForFraming, shouldSkipOpeningFraming } from "./shouldSkipWallOpeningFraming";
import {
  buildWallOpeningFramingConfigGroup,
  pushMissingFramingComponentIssue,
  validateWallOpeningFramingConfigGroup,
} from "./wallOpeningFramingConfigTools";
import { buildOpeningPlacementsByOpeningId, createWallOpeningFramingResolver } from "./wallOpeningFramingResolver";
import {
  buildOpeningSourceByOpeningId,
  resolveWallFramingMeta,
  wallPlaneSideRoomsForMember,
  wallPlaneSideRoomsForSubdomains,
} from "./wallPlaneFramingMetaResolver";

const DEFAULT_WALL_STUD_DIRECTION = Math.PI / 2;

/**
 * Two cells are connected when their AABBs share an edge with a positive overlap length. The
 * subdomain partition is non-uniform (a window column is split into bottom/void/top while the
 * neighboring full-height columns stay single cells), so grid `u`/`v` indices do not reliably
 * reflect adjacency. Compare geometry directly; corner-only touches do not connect.
 */
function gridCellsShareEdge(a: AABB2, b: AABB2): boolean {
  const shareVerticalEdge =
    (Math.abs(a.maxX - b.minX) <= DIST_EPSILON || Math.abs(b.maxX - a.minX) <= DIST_EPSILON) &&
    Math.min(a.maxY, b.maxY) - Math.max(a.minY, b.minY) > DIST_EPSILON;
  const shareHorizontalEdge =
    (Math.abs(a.maxY - b.minY) <= DIST_EPSILON || Math.abs(b.maxY - a.minY) <= DIST_EPSILON) &&
    Math.min(a.maxX, b.maxX) - Math.max(a.minX, b.minX) > DIST_EPSILON;
  return shareVerticalEdge || shareHorizontalEdge;
}

function voidCellSitsAboveSolid(voidCell: FramingGridCell, solidCell: FramingGridCell): boolean {
  if (!gridCellsShareEdge(voidCell.aabb, solidCell.aabb)) {
    return false;
  }
  return Math.abs(voidCell.aabb.minY - solidCell.aabb.maxY) <= DIST_EPSILON;
}

function voidCellSitsBelowSolid(voidCell: FramingGridCell, solidCell: FramingGridCell): boolean {
  if (!gridCellsShareEdge(voidCell.aabb, solidCell.aabb)) {
    return false;
  }
  return Math.abs(voidCell.aabb.maxY - solidCell.aabb.minY) <= DIST_EPSILON;
}

function voidCellHasJambsOnBothSides(
  voidCell: FramingGridCell,
  borderingSolidCells: readonly FramingGridCell[],
): boolean {
  let hasLeft = false;
  let hasRight = false;

  for (const solidCell of borderingSolidCells) {
    const voidAabb = voidCell.aabb;
    const solidAabb = solidCell.aabb;
    const shareVerticalEdge =
      (Math.abs(voidAabb.maxX - solidAabb.minX) <= DIST_EPSILON ||
        Math.abs(solidAabb.maxX - voidAabb.minX) <= DIST_EPSILON) &&
      Math.min(voidAabb.maxY, solidAabb.maxY) - Math.max(voidAabb.minY, solidAabb.minY) > DIST_EPSILON;
    if (!shareVerticalEdge) {
      continue;
    }
    if (solidAabb.maxX <= voidAabb.minX + DIST_EPSILON) {
      hasLeft = true;
    }
    if (solidAabb.minX >= voidAabb.maxX - DIST_EPSILON) {
      hasRight = true;
    }
  }

  return hasLeft && hasRight;
}

/**
 * One-sided wall height cut: void stacked on only one side of bordering solid (top- or bottom-anchored).
 * Enclosed voids with solid both above and below are intentionally not height cuts — they stay in
 * collectRegionCutVoidCells and are framed as window openings (mid-wall cut rules without placements).
 * Base-edge door notches are also one-sided vertically but have jambs on both sides; those stay openings.
 */
function isWallHeightCutVoid(voidCell: FramingGridCell, borderingSolidCells: readonly FramingGridCell[]): boolean {
  const sitsAbove = borderingSolidCells.some((solidCell) => voidCellSitsAboveSolid(voidCell, solidCell));
  const sitsBelow = borderingSolidCells.some((solidCell) => voidCellSitsBelowSolid(voidCell, solidCell));
  const oneSided = (sitsAbove && !sitsBelow) || (sitsBelow && !sitsAbove);
  if (!oneSided) {
    return false;
  }
  if (voidCellHasJambsOnBothSides(voidCell, borderingSolidCells)) {
    return false;
  }
  return true;
}

export function flattenFramingLayerBucketToGridCells(bucket: WallPlaneAssemblyLayerBucketGrid): FramingGridCell[] {
  const cells: FramingGridCell[] = [];

  for (const [v, row] of bucket) {
    for (const [u, plane] of row) {
      if (plane.layer.layerType !== "FRAMING") {
        continue;
      }
      cells.push({
        u,
        v,
        aabb: plane.subdomainMergedAABB,
        subdomains: plane.subdomains,
      });
    }
  }

  return cells;
}

/**
 * Every plane in a layer bucket shares the same framing layer and mid-plane offset (the bucket is
 * keyed by layer identity + offset), so the placement is read from the first FRAMING plane.
 */
function framingLayerPlacementFromBucket(bucket: WallPlaneAssemblyLayerBucketGrid): FramingLayerPlacement | null {
  for (const row of bucket.values()) {
    for (const plane of row.values()) {
      if (plane.layer.layerType !== "FRAMING") {
        continue;
      }
      return {
        layer: plane.layer,
        layerCenterOffset: plane.layerCenterOffset,
        layerThickness: plane.layerThickness,
      };
    }
  }

  return null;
}

export function collectVoidGridCellsFromDescriptor(descriptor: WallPlaneDescriptor): FramingGridCell[] {
  const cells: FramingGridCell[] = [];

  for (const domain of descriptor.subdomainIndex.all()) {
    if (domain.wallInfo !== null) {
      continue;
    }
    cells.push({
      u: domain.arrayIndexU,
      v: domain.arrayIndexV,
      aabb: domain,
      subdomains: [domain],
    });
  }

  return cells;
}

/**
 * The cut source id is the deterministic opening identity. Returns undefined for
 * non-cut voids (e.g. artifacts of joined framing areas).
 */
export function openingIdForVoidCell(cell: FramingGridCell): string | undefined {
  for (const subdomain of cell.subdomains) {
    for (const operation of subdomain.operations) {
      if (operation.wallPlaneOperator.operationType === "SET_WALL_CUT") {
        return operation.source.id;
      }
    }
  }
  return undefined;
}

interface CutVoidCellCenter {
  readonly openingId: string;
  readonly cx: number;
  readonly cy: number;
}

export function assignHoleOpeningIds(
  holes: readonly ClosedPolyLine2[],
  voidCells: readonly FramingGridCell[],
): (string | undefined)[] {
  const cutCells: CutVoidCellCenter[] = [];
  for (const cell of voidCells) {
    const openingId = openingIdForVoidCell(cell);
    if (openingId === undefined) {
      continue;
    }
    const [cx, cy] = aabb2Center(cell.aabb);
    cutCells.push({ openingId, cx, cy });
  }

  return holes.map((hole) => {
    for (const cell of cutCells) {
      const containment = classifyContainmentVec2PolyLine2(cell.cx, cell.cy, hole);
      if (
        containment === Vec2PolyLine2ContainmentClass.INSIDE ||
        containment === Vec2PolyLine2ContainmentClass.COINCIDENT
      ) {
        return cell.openingId;
      }
    }
    return undefined;
  });
}

/**
 * Union the cell AABBs into a single polygon. The outer loop is the framing perimeter; inner loops
 * are enclosed voids (window openings) that the union reports as holes. This runs per connected
 * component, so the union is a single polygon. Non-convex perimeters are split into convex bays
 * downstream by `generateWallFramingWithConvexSplit` (this is an added safety check to ensure the perimeter is convex as framing generation will not work with non-convex perimeters).
 */
export function gridCellUnionToPolygon(cells: readonly { readonly aabb: AABB2 }[]): GridCellUnionPolygon | null {
  if (cells.length === 0) {
    return null;
  }

  const polygons: PolyLine2Polygon[] = cells.map((cell) => [polyLine2FromAABB2(cell.aabb)]);
  const unionResult = polygonUnion(polygons, true);
  if (unionResult.length === 0 || unionResult[0].length === 0) {
    return null;
  }

  const [exteriorLoop, ...holeLoops] = unionResult[0];
  return {
    exterior: polyLine2CopySetCCW(closedPolyLine2New(exteriorLoop)),
    holes: holeLoops.map((hole) => polyLine2CopySetCW(closedPolyLine2New(hole))),
  };
}

/**two local helper functions to set the winding direction of a branded polyline2 */
function polyLine2CopySetCCW(pl: ClosedPolyLine2): ClosedPolyLine2 {
  const copy = closedPolyLine2New(polyLine2Copy(pl));
  polyLine2SetCCW(copy);
  return copy;
}

function polyLine2CopySetCW(pl: ClosedPolyLine2): ClosedPolyLine2 {
  const copy = closedPolyLine2New(polyLine2Copy(pl));
  polyLine2SetCW(copy);
  return copy;
}

/**
 * Group solid cells into connected components using geometric edge-adjacency (BFS). Each component
 * becomes one framing region whose exterior perimeter is the union of its cells (non-convex
 * allowed); the convex split downstream handles bay generation and enclosed voids become openings.
 */
export function groupSolidCellsIntoConnectedComponents(cells: readonly FramingGridCell[]): FramingGridCell[][] {
  const components: FramingGridCell[][] = [];
  const visited = new Set<number>();

  for (let i = 0; i < cells.length; i++) {
    if (visited.has(i)) {
      continue;
    }

    const component: FramingGridCell[] = [];
    const queue: number[] = [i];
    visited.add(i);

    while (queue.length > 0) {
      const currentIndex = queue.pop();
      assertIsNotNullOrUndefined(currentIndex);
      const current = cells[currentIndex];
      assertIsNotNullOrUndefined(current);
      component.push(current);

      for (let j = 0; j < cells.length; j++) {
        if (visited.has(j)) {
          continue;
        }
        const candidate = cells[j];
        assertIsNotNullOrUndefined(candidate);
        if (gridCellsShareEdge(current.aabb, candidate.aabb)) {
          visited.add(j);
          queue.push(j);
        }
      }
    }

    components.push(component);
  }

  return components;
}

function voidCellsForOpeningId(openingId: string, voidCells: readonly FramingGridCell[]): readonly FramingGridCell[] {
  return voidCells.filter((voidCell) => openingIdForVoidCell(voidCell) === openingId);
}

function openingIdIsWallHeightCutForRegion(
  openingId: string,
  voidCells: readonly FramingGridCell[],
  solidCells: readonly FramingGridCell[],
): boolean {
  for (const voidCell of voidCellsForOpeningId(openingId, voidCells)) {
    const borderingSolidCells = solidCells.filter((solidCell) => gridCellsShareEdge(solidCell.aabb, voidCell.aabb));
    if (borderingSolidCells.length === 0) {
      continue;
    }
    if (isWallHeightCutVoid(voidCell, borderingSolidCells)) {
      return true;
    }
  }
  return false;
}

/**
 * Cut void cells (doors/windows) that border this region's solid cells. A door at the wall base sits
 * on the perimeter edge, so a solid-only union reports it as a notch in the exterior loop rather than
 * an enclosed hole. Collecting these lets the region fill the notch and frame the door as an opening.
 * One-sided wall height cut voids are excluded so the kept solid perimeter stays at cut height and
 * caps with a plate instead of unioning through the cut band. Enclosed voids (solid above and below)
 * remain included and are framed as windows.
 * Non-cut voids have no opening id and are excluded so they stay outside the perimeter instead of being filled in.
 */
export function collectRegionCutVoidCells(
  solidCells: readonly FramingGridCell[],
  voidCells: readonly FramingGridCell[],
): FramingGridCell[] {
  return voidCells.filter((voidCell) => {
    if (openingIdForVoidCell(voidCell) === undefined) {
      return false;
    }

    const borderingSolidCells = solidCells.filter((solidCell) => gridCellsShareEdge(solidCell.aabb, voidCell.aabb));
    if (borderingSolidCells.length === 0) {
      return false;
    }

    if (isWallHeightCutVoid(voidCell, borderingSolidCells)) {
      return false;
    }

    return true;
  });
}

/**
 * Resolve each opening's two flanking rooms from the solid subdomains that border its void cells.
 */
export function buildOpeningSidesByOpeningId(
  solidCells: readonly FramingGridCell[],
  voidCells: readonly FramingGridCell[],
  buildingGraph: BuildingGraph,
  rooms: Table<Room>,
): Map<string, WallPlaneSides> {
  const faceNodeIdsByOpeningId = new Map<string, string[]>();

  for (const voidCell of voidCells) {
    const openingId = openingIdForVoidCell(voidCell);
    if (openingId === undefined) {
      continue;
    }
    const faceNodeIds = faceNodeIdsByOpeningId.get(openingId) ?? [];
    for (const solidCell of solidCells) {
      if (!gridCellsShareEdge(solidCell.aabb, voidCell.aabb)) {
        continue;
      }
      for (const subdomain of solidCell.subdomains) {
        faceNodeIds.push(...subdomain.faceNodeIds);
      }
    }
    faceNodeIdsByOpeningId.set(openingId, faceNodeIds);
  }

  const sidesByOpeningId = new Map<string, WallPlaneSides>();
  for (const [openingId, faceNodeIds] of faceNodeIdsByOpeningId) {
    sidesByOpeningId.set(openingId, resolveWallPlaneSideRoomsFromFaceNodes(faceNodeIds, buildingGraph, rooms));
  }

  return sidesByOpeningId;
}

/**
 * Build a framing region's outer perimeter and its openings from the regions solid cells plus the
 * cut void cells that border them. Door/window cut voids are unioned into the perimeter so base-edge
 * doors no longer notch the outer boundary; each is then framed as an opening. One-sided wall height
 * cut voids trim the exterior only (Case B) and are omitted from openings; enclosed voids (Cases A/C)
 * remain in collectRegionCutVoidCells and become window openings.
 */
export function buildRegionFramingPerimeterAndOpenings(
  solidCells: readonly FramingGridCell[],
  voidCells: readonly FramingGridCell[],
): { exterior: ClosedPolyLine2; openings: WallOpeningBoundary[] } | null {
  const regionCutVoidCells = collectRegionCutVoidCells(solidCells, voidCells);

  const perimeterPolygon = gridCellUnionToPolygon([...solidCells, ...regionCutVoidCells]);
  if (!perimeterPolygon) {
    return null;
  }

  const openings: WallOpeningBoundary[] = [];

  const voidCellsByOpeningId = new Map<string, FramingGridCell[]>();
  for (const cell of regionCutVoidCells) {
    const openingId = openingIdForVoidCell(cell);
    assertIsNotNullOrUndefined(openingId);
    const cells = voidCellsByOpeningId.get(openingId) ?? [];
    cells.push(cell);
    voidCellsByOpeningId.set(openingId, cells);
  }

  for (const [openingId, cells] of voidCellsByOpeningId) {
    const openingPolygon = gridCellUnionToPolygon(cells);
    if (!openingPolygon) {
      continue;
    }
    openings.push({ polyline: polyLine2CopySetCW(openingPolygon.exterior), openingId });
  }

  // Remaining holes are enclosed voids with no wall-cut source (e.g. joined-area artifacts); preserve
  // the prior behavior of framing them as openings with a best-effort opening id. Skip one-sided
  // wall height cut holes — those trim the region exterior and must not become openings.
  const holeOpeningIds = assignHoleOpeningIds(perimeterPolygon.holes, voidCells);
  for (let holeIndex = 0; holeIndex < perimeterPolygon.holes.length; holeIndex++) {
    const openingId = holeOpeningIds[holeIndex];
    if (openingId !== undefined && openingIdIsWallHeightCutForRegion(openingId, voidCells, solidCells)) {
      continue;
    }
    openings.push({
      polyline: perimeterPolygon.holes[holeIndex],
      openingId,
    });
  }

  return { exterior: perimeterPolygon.exterior, openings };
}

export function resolveLevelIdForFramingRegion(
  subdomains: readonly WallPlaneSubdomain[],
  buildingGraph: BuildingGraph,
  rooms: Building["rooms"],
  doc: Doc,
  levels: Table<Level>,
): string | undefined {
  const filterBuilding: BuildingGraphFilterBuilding = {
    buildingGraph,
    rooms,
    doc,
  };

  const levelIds = new Set<string>();
  for (const subdomain of subdomains) {
    for (const faceNodeId of subdomain.faceNodeIds) {
      const faceNode = buildingGraph.faceNodes[faceNodeId];
      if (!faceNode) {
        continue;
      }
      const levelId = faceNodeGetLevelId(faceNode, filterBuilding);
      if (levelId) {
        levelIds.add(levelId);
      }
    }
  }

  for (const levelId of sortedLevelIds(levels)) {
    if (levelIds.has(levelId)) {
      return levelId;
    }
  }

  return undefined;
}

function splitRegionPickAABB(splitRegion: PlanarTopologySplitRegion): AABB2 {
  return {
    minX: splitRegion.uMin,
    minY: splitRegion.vMin,
    maxX: splitRegion.uMax,
    maxY: splitRegion.vMax,
  };
}

function framingRegionOverlapsSplitRegion(region: FramingRegion, splitRegion: PlanarTopologySplitRegion): boolean {
  const splitRegionAabb = splitRegionPickAABB(splitRegion);
  for (const cell of region.solidCells) {
    if (aabb2Overlaps(cell.aabb, splitRegionAabb)) {
      return true;
    }
  }
  return false;
}

/** True when a framing region's solid cells share face nodes with the pick region's topology. */
function framingRegionTouchesTopology(
  region: FramingRegion,
  topologyId: string,
  buildingGraph: BuildingGraph,
): boolean {
  const topology = buildingGraph.faceTopologies[topologyId];
  if (!topology) {
    return false;
  }

  const topologyFaceNodeIds = new Set(topology.faceNodeIds);
  for (const cell of region.solidCells) {
    for (const subdomain of cell.subdomains) {
      for (const faceNodeId of subdomain.faceNodeIds) {
        if (topologyFaceNodeIds.has(faceNodeId)) {
          return true;
        }
      }
    }
  }
  return false;
}

function pickFaceSpecFromPickRegion(
  pickRegion: PlanarTopologySplitPickRegion,
  planeHash: string,
): WallFramingPickFaceSpec {
  const { splitRegion, topologyId, pickHandle } = pickRegion;
  return {
    topologyId,
    planeHash,
    uMin: splitRegion.uMin,
    uMax: splitRegion.uMax,
    vMin: splitRegion.vMin,
    vMax: splitRegion.vMax,
    pickHandle,
    startHorizontalDatumId: splitRegion.startHorizontalDatumId,
    endHorizontalDatumId: splitRegion.endHorizontalDatumId,
    startVerticalDatumId: splitRegion.startVerticalDatumId,
    endVerticalDatumId: splitRegion.endVerticalDatumId,
  };
}

/**
 * Empty-core (and other non-framed) assembly regions still need GPU pick faces. Attach any
 * unassigned pick regions to the wall-framing region pick-face table so 3D selection stays available.
 */
function attachUnassignedPickFacesWithoutFraming(
  wallFramingRegionPickFaces: RWTable<WallFramingPickFaceSpec[]>,
  pickRegionsByPlaneHash: Map<string, PlanarTopologySplitPickRegion[]>,
  assignedPickIds: Set<string>,
  buildingGraph: BuildingGraph,
  rooms: Table<Room>,
  doc: Doc,
  levels: Table<Level>,
): void {
  const filterBuilding: BuildingGraphFilterBuilding = {
    buildingGraph,
    rooms,
    doc,
  };

  for (const [planeHash, planePickRegions] of pickRegionsByPlaneHash) {
    for (const pickRegion of planePickRegions) {
      if (assignedPickIds.has(docEntityHandleHash(pickRegion.pickHandle))) {
        continue;
      }

      const topology = buildingGraph.faceTopologies[pickRegion.topologyId];
      if (!topology) {
        continue;
      }

      const topologyLevelIds = new Set<string>();
      for (const faceNodeId of topology.faceNodeIds) {
        const faceNode = buildingGraph.faceNodes[faceNodeId];
        if (!faceNode) {
          continue;
        }
        const faceNodeLevelId = faceNodeGetLevelId(faceNode, filterBuilding);
        if (faceNodeLevelId) {
          topologyLevelIds.add(faceNodeLevelId);
        }
      }

      const levelId = sortedLevelIds(levels).find(
        (candidateLevelId) =>
          topologyLevelIds.has(candidateLevelId) && wallFramingRegionPickFaces[candidateLevelId] !== undefined,
      );
      if (!levelId) {
        continue;
      }

      assignedPickIds.add(docEntityHandleHash(pickRegion.pickHandle));
      wallFramingRegionPickFaces[levelId].push(pickFaceSpecFromPickRegion(pickRegion, planeHash));
    }
  }
}

function defaultWallOpeningFramingConfigGroup(
  inheritedStudComponentId: WallPlaneFramingLayer["framingComponentId"],
  headerAssemblies: Table<HeaderAssemblyEntity>,
): WallOpeningFramingConfigGroup {
  return buildWallOpeningFramingConfigGroup(inheritedStudComponentId, undefined, headerAssemblies);
}

export function wallFramingPlaneForLayer(buildingPlane: BuildingGraphPlane, layerCenterOffset: number): Plane {
  return planeMoveAlongNormal(planeCopy(buildingPlane.planeFace.plane), layerCenterOffset);
}

/**
 * Attach the assembly framing layer's top/bottom plate counts to the perimeter as per-edge framing
 * config. The generator stacks plates from `framingConfig.count` on edges classified as top/bottom
 * plate, so without this every wall defaults to a single top and bottom plate. Existing edge metadata
 * is preserved; only the plate `count` is set.
 */
export function applyPlateCountsToPerimeter(
  perimeter: FramingBoundaryWithMetadata,
  layer: WallPlaneFramingLayer,
): FramingBoundaryWithMetadata {
  const metadata: Record<number, EdgeMetadata> = { ...perimeter.edgeMetadata };

  for (let i = 0, l = polyLine2EdgeCount(perimeter.polyline); i < l; i++) {
    const edgeType = classifyBoundaryEdgeType(perimeter.polyline, i);
    let count: number | undefined;
    if (edgeType === WallFramingType.BOTTOM_PLATE) {
      count = layer.bottomPlateCount;
    } else if (edgeType === WallFramingType.TOP_PLATE) {
      count = layer.topPlateCount;
    }
    if (count === undefined) {
      continue;
    }
    metadata[i] = {
      ...metadata[i],
      framingConfig: { ...metadata[i]?.framingConfig, count },
    };
  }

  const edgeMetadata: EdgeMetadataTable = metadata;
  return { ...perimeter, edgeMetadata };
}

export function buildWallFramingInputFromRegion(
  region: FramingRegion,
  exterior: FramingBoundaryWithMetadata,
  openings: readonly WallOpeningBoundary[],
  framingComponents: Table<FramingComponentEntity>,
  buildingPlane: BuildingGraphPlane,
  headerAssemblies: Table<HeaderAssemblyEntity>,
  resolveOpeningFramingConfig: WallOpeningFramingResolver | undefined,
  issues: BuildingGenerationIssue[],
): WallFramingInput | null {
  const inheritedStudComponentId = region.layer.framingComponentId;
  const defaultFramingDescriptor = getFramingProfileDescriptorById(framingComponents, inheritedStudComponentId);
  if (!defaultFramingDescriptor) {
    pushMissingFramingComponentIssue(issues, inheritedStudComponentId, "Wall framing region skipped");
    return null;
  }

  const baseOpeningConfig = defaultWallOpeningFramingConfigGroup(inheritedStudComponentId, headerAssemblies);
  validateWallOpeningFramingConfigGroup(
    baseOpeningConfig,
    framingComponents,
    issues,
    "Wall framing region default opening config",
  );

  const direction = DEFAULT_WALL_STUD_DIRECTION;
  const framingPlane = wallFramingPlaneForLayer(buildingPlane, region.layerCenterOffset);
  const perimeter = applyPlateCountsToPerimeter(exterior, region.layer);

  return {
    perimeter,
    framingMetadata: {
      direction,
      framingStartPt: computeWallFramingStartPt(perimeter.polyline, direction),
      spacing: region.layer.studSpacing,
      defaultFramingDescriptor,
      forceTrimmersAtOpeningEnds: false,
    },
    openings,
    openingFramingConfigGroup: baseOpeningConfig,
    resolveOpeningFramingConfig,
    framingPlane,
    framingPlaneNormalOffset: region.layerThickness / 2,
  };
}

export function generatedAssemblyToWallFraming(
  assembly: GeneratedFramedWallAssembly,
  resolveMeta?: (member: Framing) => WallFramingMeta | undefined,
): WallFraming {
  // A header's depth-stacked plies are generated as separate members sharing a headerGroupId. Collect
  // each group's rails so the plies emit as one output member holding all plies as rails — matching the
  // legacy pipeline's one-member-N-rails shape (and keeping consumers like the BAPI header count stable
  // across the wallPlanes feature flag).
  const headerRailsByGroupId = new Map<string, Framing["rails"]>();
  for (const member of assembly.framing) {
    if (member.type === WallFramingType.HEADER && member.headerGroupId !== undefined) {
      const groupRails = headerRailsByGroupId.get(member.headerGroupId);
      if (groupRails) {
        groupRails.push(...member.rails);
      } else {
        headerRailsByGroupId.set(member.headerGroupId, [...member.rails]);
      }
    }
  }

  const emittedHeaderGroupIds = new Set<string>();
  const framings: OutputWallFramingMember[] = [];
  for (const member of assembly.framing) {
    if (member.type === WallFramingType.HEADER && member.headerGroupId !== undefined) {
      // Skip the follow-on plies of a header group; the first ply carries the merged rails.
      if (emittedHeaderGroupIds.has(member.headerGroupId)) {
        continue;
      }
      emittedHeaderGroupIds.add(member.headerGroupId);
      const mergedRails = headerRailsByGroupId.get(member.headerGroupId) ?? member.rails;
      const meta = resolveMeta ? resolveMeta(member) : member.meta;
      framings.push(adaptFramingMemberToWallFramingOutput({ ...member, rails: mergedRails }, meta));
      continue;
    }
    const meta = resolveMeta ? resolveMeta(member) : member.meta;
    framings.push(adaptFramingMemberToWallFramingOutput(member, meta));
  }
  return wallFramingNew(framings);
}

function adaptFramingMemberToWallFramingOutput(
  member: Framing,
  meta: WallFramingMeta | undefined,
): OutputWallFramingMember {
  const { id, type, profileDescriptor, rails } = member;
  const outputProfileDescriptor = rectangularLumberDescriptor(profileDescriptor);
  const outputMeta = wallFramingMetaToBuildingOutput(meta);

  if (type === WallFramingType.HEADER) {
    return {
      id,
      type: OutputWallFramingType.HEADER,
      profileDescriptor: outputProfileDescriptor,
      rails,
      meta: outputMeta,
    };
  }

  return {
    type: type as OutputWallFramingType,
    profileDescriptor: outputProfileDescriptor,
    rails,
    meta: outputMeta,
  } as OutputWallFramingMember;
}

function wallFramingMetaToBuildingOutput(meta: WallFramingMeta | undefined): OutputWallFramingMeta | undefined {
  if (!meta) {
    return undefined;
  }
  const { openingIds: strippedOpeningIds, ...outputMeta } = meta;
  return outputMeta;
}

function isVerticalWallPlane(buildingPlane: BuildingGraphPlane, filterBuilding: BuildingGraphFilterBuilding): boolean {
  const angle = planeNormalToZAxisAngle(buildingPlane, filterBuilding);
  if (angle === undefined) {
    return false;
  }
  return getBuildingGraphPlaneTypeFromNormalToZAxisAngle(angle) === "VERTICAL";
}

function buildFramingRegionsForWallPlane(
  planeHash: string,
  framingLayerBuckets: Map<string, WallPlaneAssemblyLayerBucketGrid>,
): FramingRegion[] {
  const regions: FramingRegion[] = [];

  for (const [layerHash, bucket] of framingLayerBuckets.entries()) {
    const placement = framingLayerPlacementFromBucket(bucket);
    if (!placement) {
      continue;
    }

    const solidCells = flattenFramingLayerBucketToGridCells(bucket);
    if (solidCells.length === 0) {
      continue;
    }

    const components = groupSolidCellsIntoConnectedComponents(solidCells);
    for (const component of components) {
      regions.push({
        planeHash,
        layerHash,
        ...placement,
        solidCells: component,
      });
    }
  }

  return regions;
}

interface MakeBuildingWallFramingFromWallPlanesParams
  extends Pick<
      Building,
      | "buildingGraph"
      | "wallPlanes"
      | "wallPlaneOpeningDomainOperations"
      | "resolvedDatums"
      | "datumAbstractions"
      | "levels"
      | "rooms"
      | "roomFloorAssemblies"
      | "settings"
      | "windowPlacements"
      | "doorPlacements"
    >,
    Pick<
      Doc,
      | "framingComponents"
      | "wallPlaneAssemblies"
      | "masonryComponents"
      | "cipComponents"
      | "sheathingComponents"
      | "headerAssemblies"
    > {
  readonly doc: Doc;
  readonly headerFramingSettings: HeaderFramingSettings;
  readonly targetedOpeningSettings: TargetedOpeningSettings;
}

export function makeBuildingWallFramingFromWallPlanes({
  buildingGraph,
  wallPlanes,
  wallPlaneOpeningDomainOperations,
  resolvedDatums,
  datumAbstractions,
  levels,
  rooms,
  windowPlacements,
  doorPlacements,
  doc,
  framingComponents,
  wallPlaneAssemblies,
  masonryComponents,
  cipComponents,
  sheathingComponents,
  headerAssemblies,
  headerFramingSettings,
  targetedOpeningSettings,
}: MakeBuildingWallFramingFromWallPlanesParams): {
  readonly wallFramingSystems: RWTable<WallFraming[]>;
  readonly wallFramingRegionPickFaces: RWTable<WallFramingPickFaceSpec[]>;
  readonly issues: BuildingGenerationIssue[];
} {
  const wallFramingSystems: RWTable<WallFraming[]> = {};
  const wallFramingRegionPickFaces: RWTable<WallFramingPickFaceSpec[]> = {};
  const issues: BuildingGenerationIssue[] = [];
  const allWallPlaneAssemblies = { ...builtInWallPlaneAssemblies, ...wallPlaneAssemblies };

  const filterBuilding: BuildingGraphFilterBuilding = {
    buildingGraph,
    rooms,
    doc,
  };

  const placementsByOpeningId = buildOpeningPlacementsByOpeningId(windowPlacements, doorPlacements);
  const openingFramingResolver = createWallOpeningFramingResolver({
    placementsByOpeningId,
    targetedOpeningSettings,
    headerAssemblies,
    framingComponents,
    headerFramingSettings,
  });

  // per-opening door/window source, used to tag member metadata later
  const openingSourceByOpeningId = buildOpeningSourceByOpeningId(windowPlacements, doorPlacements);

  for (const levelId in levels) {
    wallFramingSystems[levelId] = [];
    wallFramingRegionPickFaces[levelId] = [];
  }

  const pickRegionsByPlaneHash = new Map<string, PlanarTopologySplitPickRegion[]>();
  for (const pickRegion of enumeratePlanarTopologySplitPickRegions(
    { buildingGraph, doc, wallPlanes },
    "wallCoreOperator",
  )) {
    const topology = buildingGraph.faceTopologies[pickRegion.topologyId];
    if (!topology) {
      continue;
    }

    const planePickRegions = pickRegionsByPlaneHash.get(topology.planeHash) ?? [];
    planePickRegions.push(pickRegion);
    pickRegionsByPlaneHash.set(topology.planeHash, planePickRegions);
  }
  const assignedPickIds = new Set<string>();

  for (const planeHash in wallPlanes) {
    const wallPlane = wallPlanes[planeHash];
    const buildingPlane = buildingGraph.buildingPlanes[planeHash];
    if (!wallPlane || !buildingPlane) {
      continue;
    }

    if (!isVerticalWallPlane(buildingPlane, filterBuilding)) {
      continue;
    }

    const { buckets: framingLayerBuckets, descriptor } = collectWallPlaneFramingLayerBucketsForWallPlane(
      buildingGraph,
      wallPlane,
      wallPlaneOpeningDomainOperations[planeHash] ?? [],
      allWallPlaneAssemblies,
      framingComponents,
      masonryComponents,
      cipComponents,
      sheathingComponents,
      resolvedDatums,
      datumAbstractions,
    );

    const voidCells = collectVoidGridCellsFromDescriptor(descriptor);
    const planeSolidCells = [...framingLayerBuckets.values()].flatMap(flattenFramingLayerBucketToGridCells);
    const openingSidesByOpeningId = buildOpeningSidesByOpeningId(planeSolidCells, voidCells, buildingGraph, rooms);
    const regions = buildFramingRegionsForWallPlane(planeHash, framingLayerBuckets);

    for (const region of regions) {
      const regionSubdomains = region.solidCells.flatMap((cell) => [...cell.subdomains]);
      const levelId = resolveLevelIdForFramingRegion(regionSubdomains, buildingGraph, rooms, doc, levels);
      const planePickRegions = pickRegionsByPlaneHash.get(planeHash);
      const pickFaces: WallFramingPickFaceSpec[] = [];
      if (planePickRegions) {
        for (const pickRegion of planePickRegions) {
          if (assignedPickIds.has(docEntityHandleHash(pickRegion.pickHandle))) {
            continue;
          }
          // Coplanar topologies share a planeHash; only attach picks whose topology
          // contributes face nodes to this framing region (empty cores stay unassigned here).
          if (!framingRegionTouchesTopology(region, pickRegion.topologyId, buildingGraph)) {
            continue;
          }
          if (!framingRegionOverlapsSplitRegion(region, pickRegion.splitRegion)) {
            continue;
          }

          assignedPickIds.add(docEntityHandleHash(pickRegion.pickHandle));
          pickFaces.push(pickFaceSpecFromPickRegion(pickRegion, planeHash));
        }
      }

      // Fill door/window voids into the region perimeter (so base-edge doors don't notch the outer
      // boundary) and frame each filled void as an opening instead.
      const regionPerimeter = buildRegionFramingPerimeterAndOpenings(region.solidCells, voidCells);
      if (!regionPerimeter) {
        continue;
      }

      const exterior: FramingBoundaryWithMetadata = { polyline: regionPerimeter.exterior };

      const resolveOpeningFramingConfig = openingFramingResolver.resolveForRegion(
        region.layer.framingComponentId,
        issues,
      );

      const openings = filterWallOpeningBoundariesForFraming(
        regionPerimeter.openings,
        resolveOpeningFramingConfig,
        (openingId) => openingFramingResolver.getResolvedMap()[openingId],
        shouldSkipOpeningFraming,
      );

      const input = buildWallFramingInputFromRegion(
        region,
        exterior,
        openings,
        framingComponents,
        buildingPlane,
        headerAssemblies,
        resolveOpeningFramingConfig,
        issues,
      );
      if (!input) {
        continue;
      }

      const generated = generateWallFramingWithConvexSplit(input, framingComponents);

      if (!levelId || !wallFramingSystems[levelId]) {
        continue;
      }

      const regionSides = wallPlaneSideRoomsForSubdomains(regionSubdomains, buildingGraph, rooms);
      const regionSpacing = region.layer.studSpacing;
      const perimeterAABB = polyLine2AABB2(regionPerimeter.exterior);
      const regionWallHeight = perimeterAABB.maxY - perimeterAABB.minY;
      const resolvedFramingByOpeningId = openingFramingResolver.getResolvedMap();
      const wallFraming = generatedAssemblyToWallFraming(generated, (member) => {
        // Opening members take the per-opening sides resolved from the bordering solid subdomains;
        // all other members resolve from their own sampled subdomain with a region-level fallback.
        const primaryOpeningId = member.meta?.openingIds?.[0];
        const openingSides = primaryOpeningId !== undefined ? openingSidesByOpeningId.get(primaryOpeningId) : undefined;
        const sides =
          openingSides ??
          wallPlaneSideRoomsForMember({
            member,
            descriptor,
            buildingPlane,
            buildingGraph,
            rooms,
            regionFallback: regionSides,
          });
        return resolveWallFramingMeta({
          member,
          sides,
          wallHeight: regionWallHeight,
          spacing: regionSpacing,
          resolvedFramingByOpeningId,
          openingSourceByOpeningId,
        });
      });
      wallFramingSystems[levelId].push(wallFraming);
      if (pickFaces.length > 0) {
        wallFramingRegionPickFaces[levelId].push(...pickFaces);
      }
    }
  }

  attachUnassignedPickFacesWithoutFraming(
    wallFramingRegionPickFaces,
    pickRegionsByPlaneHash,
    assignedPickIds,
    buildingGraph,
    rooms,
    doc,
    levels,
  );

  return { wallFramingSystems, wallFramingRegionPickFaces, issues };
}
